import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  getIndexedSharedDrivesWithChangeLog,
  getUserPermission,
  UserKeys,
} from '../../../api/users-api';
import { BigQueryPage, SharedDriveModel } from '../../../../types';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { handleErrors } from '../../../utils/errors';
import SharedErrorBoundary from '../../../shared/shared-error-boundary';
import SharedDrivesCreateDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-create-dialog';
import SharedDrivesRenameDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-rename-dialog';
import SharedDrivesDeleteDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-delete-dialog';
import SharedDrivesSettingsDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-settings-dialog';
import SharedDrivesPermissionDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-permission-dialog';
import SharedDrivesIndexedGridToolbar from './shared-drives-indexed-toolbar';
import { bigQueryFilterOperators } from '../../../shared/big-query-filter-operators';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import ChangeLogRequiredOverlay from '../../grid-overlay/change-log-required-overlay';
import AvatarChip from '../../../shared/avatar-chip';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SharedDrivesChildGrid from '../../user-management/user-details/drive/shared-drives/shared-drives-child/shared-drives-child-grid';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import { isMissingBigQueryTableError } from '../../../utils/error-utils';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { useCopySharedDriveId } from './use-copy-shared-drive-id';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddToDrive from '@mui/icons-material/AddToDrive';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Help from '@mui/icons-material/Help';
import Link from '@mui/icons-material/Link';
import Settings from '@mui/icons-material/Settings';
import Share from '@mui/icons-material/Share';
interface SharedDrivesIndexedGridProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  selectedDrive: SharedDriveModel | null;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  permissionCheckLoading: boolean;
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
  selectedFolder: string;
  canFetch?: boolean;
  confirmedChangeLogMessage?: string;
  showChangeLogOverlay?: boolean;
  onOpenChangeLogDialog?: () => void;
}

const transformFilterModelForApi = (
  model: GridFilterModel
): GridFilterModel => {
  const updateItem = (item: GridFilterItem): GridFilterItem | null => {
    if (item.field === 'createdDate' && item.value) {
      if (item.operator === 'after' || item.operator === 'onOrBefore') {
        const timeString = new Date(item.value);
        timeString.setUTCHours(23, 59, 59, 999);
        const endOfDay = timeString.getTime().toString();
        return { ...item, field: 'createdTime', value: endOfDay };
      } else {
        const timeString = new Date(item.value).getTime().toString();
        return { ...item, field: 'createdTime', value: timeString };
      }
    } else if (item.field === 'id' && item.value) {
      return { ...item, field: 'driveId' };
    } else {
      return item;
    }
  };

  const quickFilterValue = model.quickFilterValues?.[0];

  const processedItems = (model.items || [])
    .map(updateItem)
    .filter((item): item is GridFilterItem => item !== null);

  if (quickFilterValue && quickFilterValue.length > 0) {
    const nameFilterIndex = processedItems.findIndex(
      (item) => item.field === 'name'
    );
    if (nameFilterIndex > -1) {
      processedItems[nameFilterIndex].value = quickFilterValue;
    } else {
      processedItems.push({
        field: 'name',
        operator: 'contains',
        value: quickFilterValue,
      });
    }
  }

  return { ...model, items: processedItems };
};

export default function SharedDrivesIndexedGrid({
  userEmail,
  primaryDomain,
  driveRoles,
  changeLogRoles,
  setSelectedFolder,
  setOpenPermissionCheckDialog,
  selectedDrive,
  setSelectedDrive,
  permissionCheckLoading,
  setPermissionCheckLoading,
  selectedFolder,
  canFetch,
  confirmedChangeLogMessage,
  showChangeLogOverlay = false,
  onOpenChangeLogDialog,
}: SharedDrivesIndexedGridProps) {
  const queryClient = useQueryClient();
  const { loggedInUser } = useUserSettings();
  const [openCreateSharedDriveDialog, setOpenCreateSharedDriveDialog] =
    useState(false);
  const [openSharedDrivesRenameDialog, setOpenSharedDrivesRenameDialog] =
    useState(false);
  const [openDeleteSharedDrivesDialog, setOpenDeleteSharedDrivesDialog] =
    useState(false);
  const [openSharedDrivesSettingsDialog, setOpenSharedDrivesSettingsDialog] =
    useState(false);
  const [
    openSharedDrivesPermissionsDialog,
    setOpenSharedDrivesPermissionsDialog,
  ] = useState(false);

  const canSeeSharedDrives = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.ViewSharedDrives.value
  );
  const isGateOpen = canFetch ?? !showChangeLogOverlay;
  const activeChangeLogMessage = confirmedChangeLogMessage ?? '';

  function openChangeLogDialog() {
    onOpenChangeLogDialog?.();
  }
  const { handleCopySharedDriveId, CopyChangeLogDialog } = useCopySharedDriveId(
    {
      changeLogRoles,
    }
  );

  const columns: GridColDef<SharedDriveModel>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      display: 'flex',
      filterable: false,
      renderCell: (params) => {
        const avatar = (
          <Tooltip title={params.row.name}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Avatar
                src={params.row.backgroundImageLink}
                sx={{ width: 32, height: 32 }}
              />
              <Typography variant="body2">{params.row.name}</Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      width: 150,
      type: 'date',
      display: 'flex',
      filterable: true,
      filterOperators: getGridDateOperators()
        .filter((op) =>
          bigQueryFilterOperators.acceptedDateOperators.includes(op.value)
        )
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueGetter: (_value, row) => {
        if (row.createdDate === 0) return '';
        return new Date(row.createdDate);
      },
      valueFormatter: (value) => {
        if (value === '') return '';
        return new Date(value as string).toLocaleDateString();
      },
    },
    {
      field: 'id',
      headerName: 'Shared Drive ID',
      display: 'flex',
      flex: 0.4,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((op) =>
          bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
        )
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center">
            <Tooltip arrow title="Copy">
              <span>
                <IconButton
                  disabled={
                    !isAbleToSeeUIElement(
                      driveRoles,
                      DriveRoles.CanCopyShareDriveID.value
                    )
                  }
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    handleCopySharedDriveId(params.row);
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={params.row.id}>
              <Typography variant="body2">{params.row.id}</Typography>
            </Tooltip>
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'orgUnitId',
      headerName: 'Org Unit ID',
      display: 'flex',
      minWidth: 240,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((op) =>
          bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
        )
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueGetter: (_value, row) => {
        // If orgUnitId is not set then its not in our indexing system, waiting on callbacks from google
        return row.orgUnitId && row.orgUnitId.trim() !== ''
          ? row.orgUnitId
          : 'Processing...';
      },
    },
    {
      field: 'orgUnitPath',
      headerName: 'Org Unit Path',
      display: 'flex',
      minWidth: 240,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((op) =>
          bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
        )
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueGetter: (_value, row) => {
        // If orgUnitPath is not set then it is the root, return '/' as the value
        return row.orgUnitPath && row.orgUnitPath.trim() !== ''
          ? row.orgUnitPath
          : 'Processing...';
      },
      renderCell: (params) => {
        return params.value !== 'Processing...' ? (
          <AvatarChip
            key={params.value}
            email={params.value}
            type={'OrgUnit'}
            size="medium"
          />
        ) : (
          <Tooltip title="Indexing is still processing the org unit path. This takes up to several minutes.">
            <Chip
              avatar={<Avatar children={<Help sx={{ fontSize: 16 }} />} />}
              key={params.value}
              label={params.value}
              size="medium"
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'hidden',
      headerName: 'Is Hidden',
      type: 'boolean',
      minWidth: 100,
      filterable: true,
      valueGetter: (value: string) => {
        return value;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params: GridRowParams) => {
        const buttonArray = [];

        buttonArray.push(
          <GridActionsCellItem
            label={'Share'}
            icon={<Share />}
            onClick={() => {
              setSelectedDrive(params.row);
              setOpenSharedDrivesPermissionsDialog(true);
            }}
            showInMenu
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanUpdateSharedDrivePermissions.value
              )
            }
          />
        );

        buttonArray.push(
          <GridActionsCellItem
            label={'Settings'}
            icon={<Settings />}
            onClick={() => {
              setSelectedDrive(params.row);
              setOpenSharedDrivesSettingsDialog(true);
            }}
            showInMenu
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanUpdateSharedDriveSettings.value
              )
            }
          />
        );

        buttonArray.push(
          <GridActionsCellItem
            label={'Rename'}
            icon={<DriveFileRenameOutline />}
            onClick={() => {
              setSelectedDrive(params.row);
              setOpenSharedDrivesRenameDialog(true);
            }}
            showInMenu
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanRenameSharedDrives.value
              )
            }
          />
        );

        buttonArray.push(
          <GridActionsCellItem
            label={'Delete'}
            icon={<Delete />}
            onClick={() => {
              setSelectedDrive(params.row);
              setOpenDeleteSharedDrivesDialog(true);
            }}
            showInMenu
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanDeleteSharedDrives.value
              )
            }
          />
        );

        buttonArray.push(
          <GridActionsCellItem
            label={'Link'}
            component={'a'}
            icon={
              <Tooltip title="Link" arrow>
                <Link />
              </Tooltip>
            }
            // @ts-expect-error - href is valid when using component prop
            href={`https://drive.google.com/drive/folders/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
          />
        );

        return buttonArray;
      },
    },
  ];

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'sharedDrivesIndexedGridStates'
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        orgUnitId: false,
        hidden: false,
        orgUnitPath: false,
      },
    },
  };

  const defaultFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterLogicOperator: GridLogicOperator.And,
    quickFilterValues: [],
  };

  const initialGridFilterModel =
    initialState?.filter?.filterModel || defaultFilterModel;

  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(
    initialGridFilterModel
  );
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: initialState?.sorting?.sortModel?.[0]?.field || 'name',
      sort: initialState?.sorting?.sortModel?.[0]?.sort || 'asc',
    },
  ]);

  const onFilterChange = useCallback(
    (gridFilterModel: GridFilterModel) => {
      // Helper function to update 'createdDate' to 'createdTime' and 'id' to 'driveId'
      const updateItems = (item: GridFilterItem) => {
        if (item.field === 'createdDate' && item.value) {
          if (item.operator === 'after' || item.operator === 'onOrBefore') {
            const timeString = new Date(item.value);
            timeString.setUTCHours(23, 59, 59, 999);
            const endOfDay = timeString.getTime().toString();
            return { ...item, field: 'createdTime', value: endOfDay };
          } else {
            const timeString = new Date(item.value).getTime().toString();
            return { ...item, field: 'createdTime', value: timeString };
          }
        } else if (item.field === 'id' && item.value) {
          return { ...item, field: 'driveId' };
        } else {
          return item;
        }
      };

      const removedGridItems = gridFilterModel.items.filter(
        (item) => item.value
      );
      const quickFilterValue = gridFilterModel.quickFilterValues?.[0];

      const updatedItems = removedGridItems.map((item) => {
        // If there's a quick filter value, update or add the 'name' field
        if (quickFilterValue && quickFilterValue.length > 0) {
          if (item.field === 'name') {
            return { ...item, value: quickFilterValue };
          }
          return updateItems(item);
        }
        return updateItems(item);
      });

      // If 'name' field doesn't exist, add it
      if (
        quickFilterValue &&
        !gridFilterModel.items.some((item) => item.field === 'name')
      ) {
        updatedItems.push({
          field: 'name',
          operator: 'contains',
          value: quickFilterValue,
        });
      }

      const updatedFilterModel = {
        ...gridFilterModel,
        items: updatedItems,
      };

      // Only update if the filter model has changed
      if (JSON.stringify(updatedFilterModel) !== JSON.stringify(filterModel)) {
        setFilterModel(updatedFilterModel);
      }
      // Finally, update the client filter state
      setPaginationModel((prev) => ({
        ...prev,
        page: 0,
      }));
      setGridFilterModel(gridFilterModel);
    },
    [gridFilterModel]
  );

  const onSortChange = useCallback(
    (gridSortModel: GridSortModel) => {
      // Helper function to update 'createdDate' to 'createdTime' and 'id' to 'driveId'
      const updateItems = (item: any) => {
        if (item.field === 'createdDate') {
          return { ...item, field: 'createdTime' };
        } else if (item.field === 'id') {
          return { ...item, field: 'driveId' };
        }
        {
          return item;
        }
      };

      const updatedItems = gridSortModel.map((item) => {
        return updateItems(item);
      });

      if (updatedItems.length > 0) {
        setGridSortModel(gridSortModel);
        setSortModel([...updatedItems]);
      } else {
        setGridSortModel(gridSortModel);
        setSortModel([{ field: 'name', sort: 'asc' }]);
      }
    },
    [gridSortModel]
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
    page: 0,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    transformFilterModelForApi(initialGridFilterModel)
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field:
        initialState?.sorting?.sortModel?.[0]?.field === 'createdDate'
          ? 'createdTime'
          : initialState?.sorting?.sortModel?.[0]?.field === 'id'
          ? 'driveId'
          : initialState?.sorting?.sortModel?.[0]?.field || 'name',
      sort: initialState?.sorting?.sortModel?.[0]?.sort || 'asc',
    },
  ]);

  const {
    data,
    isLoading: sharedDrivesIndexedLoading,
    isFetching: sharedDrivesIndexedFetching,
    error,
  } = useQuery({
    queryKey: [
      UserKeys.SharedDrivesIndexedPage,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
      sortModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<SharedDriveModel[]> = {
        results: [],
        pageSize: paginationModel.pageSize,
        currentPage: paginationModel.page,
        totalCount: 0,
      };
      return getIndexedSharedDrivesWithChangeLog(
        object,
        filterModel,
        sortModel,
        activeChangeLogMessage
      );
    },
    enabled: canSeeSharedDrives && isGateOpen,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('shared drives'),
    },
  });

  const isMissingTableError = error && isMissingBigQueryTableError(error);
  const overlays = !isGateOpen
    ? {
        noRowsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={openChangeLogDialog}
            permissionName="View Shared Drives"
          />
        ),
        noResultsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={openChangeLogDialog}
            permissionName="View Shared Drives"
          />
        ),
      }
    : isMissingTableError
    ? createGridOverlays(
        'Shared Drives are currently being indexed',
        <AddToDrive />,
        'The grid will refresh once complete.',
        'The grid will refresh once complete.',
        true
      )
    : createGridOverlays(
        'Shared Drives',
        <AddToDrive />,
        'Try updating your search or filters.'
      );

  const [paginationSortModel, setPaginationSortModel] = useState<{
    column: string;
    sortDirection: 'ASC' | 'DESC';
  }>({
    column: 'name',
    sortDirection: 'ASC',
  });

  const toolbarProps = useMemo(
    () => ({
      driveRoles,
      setOpenCreateSharedDriveDialog,
    }),
    [driveRoles]
  );

  const CustomToolbar = useToolbar(
    SharedDrivesIndexedGridToolbar,
    toolbarProps
  );
  if (selectedFolder !== 'shared-drives') {
    return (
      <SharedErrorBoundary section="Shared Drives Child Grid">
        <SharedDrivesChildGrid
          userEmail={loggedInUser}
          primaryDomain={primaryDomain}
          driveRoles={driveRoles}
          changeLogRoles={changeLogRoles}
          isLoading={sharedDrivesIndexedLoading || sharedDrivesIndexedFetching}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          sharedDrives={data?.results || []}
          paginationModel={paginationSortModel}
          setPaginationModel={setPaginationSortModel}
        />
      </SharedErrorBoundary>
    );
  }

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...overlays,
        }}
        initialState={initialState || defaultGridState}
        filterOperator={[GridLogicOperator.And]}
        filterPanelSx={{
          minWidth: 800,
        }}
        keepNonExistentRowsSelected={false}
        data={data}
        onRowClick={async (params) => {
          try {
            setPermissionCheckLoading(true);

            const hasPermission = await queryClient.fetchQuery({
              queryKey: [
                UserKeys.SharedDriveUserPermission,
                userEmail,
                params.row.id,
              ],
              queryFn: () => getUserPermission(params.row.id, userEmail, true),
              retry: false,
              staleTime: 1000 * 60 * 2,
            });

            if (!hasPermission) {
              setSelectedDrive(params.row);
              setOpenPermissionCheckDialog(true);
            } else {
              setSelectedFolder(params.row.id);
            }
          } catch (e) {
            handleErrors({
              error: e,
              snackbarMessage: `There was an issue checking your permissions. Please try again.`,
            });
          } finally {
            setPermissionCheckLoading(false);
          }
        }}
        columns={computedColumns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (sharedDrivesIndexedLoading || sharedDrivesIndexedFetching) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
            });
          } else {
            setPaginationModel({
              ...newModel,
            });
          }
        }}
        filterModel={gridFilterModel}
        onFilterModelChange={(newModel) => {
          if (sharedDrivesIndexedLoading || sharedDrivesIndexedFetching) return;
          onFilterChange(newModel);
        }}
        sortModel={gridSortModel}
        onSortModelChange={(newModel) => {
          if (sharedDrivesIndexedLoading || sharedDrivesIndexedFetching) return;
          onSortChange(newModel);
        }}
        sx={{
          '.MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={
          sharedDrivesIndexedLoading ||
          sharedDrivesIndexedFetching ||
          permissionCheckLoading
        }
        disableRowSelectionOnClick
      />
      {openCreateSharedDriveDialog ? (
        <SharedErrorBoundary section="Shared Drives Create Dialog">
          <SharedDrivesCreateDialog
            open={openCreateSharedDriveDialog}
            handleClose={() => {
              setOpenCreateSharedDriveDialog(false);
            }}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesRenameDialog ? (
        <SharedErrorBoundary section="Shared Drives Rename Dialog">
          <SharedDrivesRenameDialog
            open={openSharedDrivesRenameDialog}
            handleClose={() => {
              setOpenSharedDrivesRenameDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openDeleteSharedDrivesDialog ? (
        <SharedErrorBoundary section="Shared Drives Delete Dialog">
          <SharedDrivesDeleteDialog
            open={openDeleteSharedDrivesDialog}
            handleClose={() => {
              setOpenDeleteSharedDrivesDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesSettingsDialog ? (
        <SharedErrorBoundary section="Shared Drives Settings Dialog">
          <SharedDrivesSettingsDialog
            open={openSharedDrivesSettingsDialog}
            handleClose={() => {
              setOpenSharedDrivesSettingsDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            primaryDomain={primaryDomain}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesPermissionsDialog ? (
        <SharedErrorBoundary section="Shared Drives Permissions Dialog (in shared-drives-grid)">
          <SharedDrivesPermissionDialog
            open={openSharedDrivesPermissionsDialog}
            handleClose={() => {
              setOpenSharedDrivesPermissionsDialog(false);
            }}
            userEmail={userEmail}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {CopyChangeLogDialog}
    </>
  );
}
